import axios from "axios";
import CryptoJS from "crypto-js";
const {
  REACT_APP_API_CARBON_API,
  REACT_APP_API_KYC_API,
  REACT_APP_PRIVATE_KEY,
  REACT_APP_IV
} = process.env;


const isLocal = process.env.NODE_ENV === "development";

export const SERVICES = {
  FKBO: "fkbo",
  KYC: "kyc",
};

const SERVICE_CONFIG = {
  [SERVICES.FKBO]: REACT_APP_API_CARBON_API,
  [SERVICES.KYC]: REACT_APP_API_KYC_API,
};

const getBaseUrl = (service) => {
  const targetService = service || SERVICES.FKBO;
  const baseUrl = SERVICE_CONFIG[targetService];

  if (!baseUrl) {
    throw new Error(`Service "${targetService}" is not configured or missing its environment variable (REACT_APP_API_*_API).`);
  }

  if (isLocal) {
    return targetService === SERVICES.KYC ? "/api/kyc" : "/api/fkbo";
  }

  return baseUrl;
};

export const FnAuthenticatedRequest = async (
  requestType,
  endpoint,
  headers,
  payload = {},
  service = SERVICES.FKBO
) => {
  try {
    let response;
    const baseUrl = getBaseUrl(service);

    if (requestType === "GET") {
      response = await axios.get(`${baseUrl}/${endpoint}`, {
        params: convertKeysToSnakeCase(payload),
        headers: {
          ...headers,
          ...(headers?.JWTToken ? { JWTToken: "Bearer " + headers.JWTToken } : {}),
          appCode: "carbon-bo",
          appVersion: "1.0",
          IV: process.env.REACT_APP_IV
        },
      });
    } else if (requestType === "POST") {
      response = await axios.post(
        `${baseUrl}/${endpoint}`,
        { data: encrypt(JSON.stringify(convertKeysToSnakeCase(payload))) },
        {
          headers: {
            ...headers,
            ...(headers?.JWTToken ? { JWTToken: "Bearer " + headers.JWTToken } : {}),
            appCode: "carbon-bo",
            appVersion: "1.0",
            IV: process.env.REACT_APP_IV
          },
        }
      );
    } else {
      throw new Error(
        "Unsupported request type. Only GET and POST are supported."
      );
    }
    // console.log(convertKeysToCamelCase(response.data))
    const decryptedRaw = decrypt(response?.data?.data);
    // console.log(`Utils.js: Decrypted raw response for ${endpoint}:`, decryptedRaw);

    let decryptedData = JSON.parse(decryptedRaw);
    let finalResponse = convertKeysToCamelCase(decryptedData);

    // console.log(`Utils.js: Final normalized response for ${endpoint}:`, finalResponse);

    // Normalize: Ensure components get { success: true, data: ... }
    if (finalResponse && typeof finalResponse === 'object' && !Array.isArray(finalResponse)) {
      return {
        ...finalResponse,
        success: finalResponse.success !== undefined ? finalResponse.success : (finalResponse.status === "success" || true),
        data: finalResponse.data !== undefined ? finalResponse.data : finalResponse
      };
    }

    // fallback for array or primitive responses
    return {
      success: true,
      data: finalResponse
    };
  } catch (error) {
    console.error(`Utils.js: Error in FnAuthenticatedRequest for ${endpoint}:`, error);
    // You can handle errors here, or let them propagate up
    throw error;
  }
};

export const upsertKycEnabledFarmers = async (payload, { jwtToken, userId }) => {
  return await FnAuthenticatedRequest(
    "POST",
    "KycEnabledFarmers",
    {
      JWTToken: jwtToken,
      userId: userId != null && userId !== undefined ? String(userId) : "",
    },
    payload,
    SERVICES.FKBO
  );
};

export const handleDateFormat = (dateParmam) => {
  try {
    const date = new Date(dateParmam);
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    const formattedDate = date.toLocaleDateString("en-GB", options); // 'en-GB' for dd/mm/yyyy format
    return formattedDate;
  } catch (error) {
    console.error(error);
  }
};

const handleDateTimeFormat = (dateParam) => {
  try {
    const date = new Date(dateParam);
    const dateFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    };
    const timeFormatOptions = {
      hour: "numeric",
      minute: "2-digit",
      hour12: false, // Include AM/PM
    };
    const formattedDate = date.toLocaleDateString("en-GB", dateFormatOptions);
    const formattedTime = date.toLocaleTimeString("en-GB", timeFormatOptions);
    const formattedDateTime = `${formattedDate} | ${formattedTime}`;
    return formattedDateTime;
  } catch (error) {
    console.error(error);
    return "-";
  }
};

export default handleDateTimeFormat;
export const handleSearchValidation = (event) => {
  const keyCode = event.keyCode || event.which;
  const keyValue = String.fromCharCode(keyCode);
  const inputValue = event.target.value + keyValue;
  if (
    !/^[a-zA-Z0-9][a-zA-Z0-9.\s]*$/.test(inputValue) || // Require at least one character before allowing white space and dots
    !/^\d{0,10}$/.test(inputValue.replace(/\D/g, "")) || // Allow up to 10 digits
    inputValue.length >= 30 || // Limit total input length
    /\.{3,}/.test(inputValue) || // Disallow consecutive dots greater than 2
    /\s{3,}/.test(inputValue) // Disallow consecutive white spaces
  ) {
    event.preventDefault();
  }
};

export const handleValidateName = (event) => {
  const keyCode = event.keyCode || event.which;
  const keyValue = String.fromCharCode(keyCode);

  // Allow only letters and whitespace
  if (!/^[a-zA-Z\s.]*$/.test(keyValue)) {
    event.preventDefault();
  }
};

export const snakeToCamel = (snakeCaseStr) => {
  return snakeCaseStr.toLowerCase().replace(/(_\w)/g, function (match) {
    return match[1].toUpperCase();
  });
};

export const convertKeysToCamelCase = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToCamelCase(item));
  } else if (data !== null && typeof data === "object") {
    const newObj = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const camelCaseKey = snakeToCamel(key);
        newObj[camelCaseKey] = convertKeysToCamelCase(data[key]);
      }
    }
    return newObj;
  } else {
    return data;
  }
};

export const camelToSnake = (camelCaseStr) => {
  return camelCaseStr.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

export const convertKeysToSnakeCase = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToSnakeCase(item));
  } else if (data !== null && typeof data === "object") {
    const newObj = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const snakeCaseKey = camelToSnake(key);
        newObj[snakeCaseKey] = convertKeysToSnakeCase(data[key]);
      }
    }
    return newObj;
  } else {
    return data;
  }
};

// Load encryption settings from environment variables
const PRIVATE_KEY = CryptoJS.enc.Base64.parse(REACT_APP_PRIVATE_KEY);
const IV = CryptoJS.enc.Base64.parse(REACT_APP_IV);

// Encrypt function
export const encrypt = (text) => {
  if (!text) throw new Error("PlainText cannot be empty");
  return CryptoJS.AES.encrypt(text, PRIVATE_KEY, { iv: IV, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }).toString();
};

// Decrypt function
export const decrypt = (encryptedText) => {
  if (!encryptedText) throw new Error("EncryptedText cannot be empty");
  return CryptoJS.AES.decrypt(encryptedText, PRIVATE_KEY, { iv: IV, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }).toString(CryptoJS.enc.Utf8);
};


// Role

export function hasRole(allowedRoles) {
  try {
    if (typeof window === "undefined") return false;

    const authData = localStorage.getItem("auth");
    if (!authData) return false;

    const parsedAuth = JSON.parse(authData);
    const userRoles = parsedAuth?.roles || [];

    // Extract role names (normalize to lowercase)
    const roleId = userRoles.map((r) => r?.roleId);

    // Return true if any allowed role is found
    return allowedRoles.some(role => roleId.includes(role));
  } catch (error) {
    console.error("Error checking role:", error);
    return false;
  }
}